import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { TrendUp, CurrencyDollar, ChartBar, Target } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_TOOLTIP_STYLE } from "../config/uiHelpers";

const Forecasting = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/forecasting`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = data ? [
    { label: "Weighted Pipeline", value: `₹${data.weighted_pipeline.toLocaleString('en-IN')}`, icon: TrendUp, color: "#002FA7" },
    { label: "Win Rate", value: `${data.win_rate}%`, icon: Target, color: "#10B981" },
    { label: "Avg Deal Size", value: `₹${data.avg_deal_size.toLocaleString('en-IN')}`, icon: CurrencyDollar, color: "#F59E0B" },
    { label: "Open Deals", value: data.open_deals_count, icon: ChartBar, color: "#DC2626" },
  ] : [];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Sales Forecasting</h1>
        <p className="text-base text-slate-700 mb-8">Revenue projections and pipeline analysis</p>

        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : data && (
          <>
            <div data-testid="forecast-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                    <Icon size={28} weight="duotone" style={{color: s.color}} className="mb-3" />
                    <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: s.color}}>{s.value}</div>
                    <div className="text-sm text-slate-600 mt-1">{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-none shadow-sm p-6">
                <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Monthly Revenue Forecast</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.monthly_forecast}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="month" stroke="#475569" />
                    <YAxis stroke="#475569" />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Projected']} />
                    <Bar dataKey="projected" fill="#002FA7" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Win/Loss</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-slate-600">Won</span>
                      <span className="text-sm font-bold text-[#10B981]">₹{data.won_value.toLocaleString('en-IN')} ({data.won_count})</span>
                    </div>
                    <div className="w-full bg-slate-200 h-3">
                      <div className="bg-[#10B981] h-3" style={{width: `${data.won_count + data.lost_count > 0 ? (data.won_count / (data.won_count + data.lost_count) * 100) : 0}%`}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-slate-600">Lost</span>
                      <span className="text-sm font-bold text-[#DC2626]">₹{data.lost_value.toLocaleString('en-IN')} ({data.lost_count})</span>
                    </div>
                    <div className="w-full bg-slate-200 h-3">
                      <div className="bg-[#DC2626] h-3" style={{width: `${data.won_count + data.lost_count > 0 ? (data.lost_count / (data.won_count + data.lost_count) * 100) : 0}%`}}></div>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-700">Total Pipeline: <span className="font-bold">₹{data.total_pipeline.toLocaleString('en-IN')}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Forecasting;
